import { useState } from 'react';
import { api, apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

type AccountType = 'CUSTOMER' | 'PROFESSIONAL';

/**
 * Public self-service account deletion page (the URL submitted to Google Play /
 * App Store). No login required — the user proves ownership with their email +
 * password, and on success the backend runs the full GDPR erasure. Works for
 * both customer and professional accounts.
 */
export default function DeleteAccountPage() {
  const [accountType, setAccountType] = useState<AccountType>('CUSTOMER');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailValid = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleDelete = async () => {
    setError(null);
    if (!emailValid(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }
    if (!confirm) {
      setError('Please confirm you understand this cannot be undone.');
      return;
    }
    const endpoint =
      accountType === 'CUSTOMER'
        ? '/customer-auth/delete-account'
        : '/professional-auth/delete-account';
    setSubmitting(true);
    try {
      await api.post(endpoint, {
        email: email.trim().toLowerCase(),
        password,
      });
      setDone(true);
    } catch (err) {
      const msg = apiErrorMessage(
        err,
        'Could not delete the account. Please check your details and try again.',
      );
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7f5] flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-[480px]">
        <div className="text-center mb-6">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[26px] leading-[34px]">
            CHERRY PICKED
          </h1>
        </div>

        <div className="rounded-2xl bg-white border border-[#ede8e9] p-6 md:p-8 shadow-sm">
          {done ? (
            <div className="text-center flex flex-col items-center gap-4">
              <div className="size-[60px] rounded-full bg-[#8e2a2f]/[0.08] flex items-center justify-center">
                <span className="material-icons text-[#8e2a2f] text-[30px]">
                  check_circle
                </span>
              </div>
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[22px]">
                Account deleted
              </h2>
              <p className="font-['DM_Sans',sans-serif] text-[#5f595b] text-[14px] leading-[22px]">
                Your account and personal data have been permanently deleted.
                Some records we're legally required to keep (e.g. transaction
                history for tax) are retained in anonymised form. This action
                can't be undone.
              </p>
            </div>
          ) : (
            <>
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[22px] mb-1">
                Delete your account
              </h2>
              <p className="font-['DM_Sans',sans-serif] text-[#5f595b] text-[14px] leading-[22px] mb-5">
                This permanently deletes your Cherry Picked account and personal
                data and cannot be undone. Enter your login details to confirm
                it's you.
              </p>

              {/* Account type */}
              <div className="mb-4">
                <label className="block font-['DM_Sans',sans-serif] text-[13px] font-bold text-[#3a3335] mb-1.5">
                  Account type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(['CUSTOMER', 'PROFESSIONAL'] as AccountType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setAccountType(t)}
                      className={`h-[44px] rounded-[10px] border text-[14px] font-['DM_Sans',sans-serif] font-bold transition-colors ${
                        accountType === t
                          ? 'border-[#8e2a2f] bg-[#8e2a2f]/[0.06] text-[#8e2a2f]'
                          : 'border-[#ede8e9] bg-white text-[#5f595b] hover:border-[#d0c4c5]'
                      }`}
                    >
                      {t === 'CUSTOMER' ? 'Customer' : 'Professional'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  placeholder="Email address"
                  className="h-[48px] rounded-[10px] border border-[#ede8e9] bg-white px-4 text-[14px] text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  placeholder="Password"
                  className="h-[48px] rounded-[10px] border border-[#ede8e9] bg-white px-4 text-[14px] text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                />
              </div>

              <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirm}
                  onChange={(e) => {
                    setConfirm(e.target.checked);
                    setError(null);
                  }}
                  className="mt-0.5 size-[18px] accent-[#8e2a2f]"
                />
                <span className="font-['DM_Sans',sans-serif] text-[13px] text-[#5f595b] leading-[20px]">
                  I understand this permanently deletes my account and data and
                  cannot be undone.
                </span>
              </label>

              {error && (
                <p className="font-['DM_Sans',sans-serif] text-[13px] text-red-600 mt-3">
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={handleDelete}
                disabled={submitting}
                className="w-full h-[50px] mt-5 rounded-[10px] bg-[#8e2a2f] text-white font-['DM_Sans',sans-serif] font-bold text-[15px] hover:bg-[#7a2428] transition-colors disabled:opacity-50"
              >
                {submitting ? 'Deleting…' : 'Delete my account'}
              </button>

              <p className="font-['DM_Sans',sans-serif] text-[12px] text-[#908e8e] leading-[18px] mt-4 text-center">
                Need help? Contact{' '}
                <a
                  href="mailto:Hello@cherrypickedservices.com"
                  className="text-[#8e2a2f] font-bold"
                >
                  Hello@cherrypickedservices.com
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
